import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@cartero/shared/solid";
import { eq, user } from "@cartero/powersync";
import { useUserPreferences } from "@/providers/user-preferences-context";
import {
  type ColorMode,
  type DesignTokens,
  getUnionThemeQuery,
  queryKeys,
  resolveTheme,
  THEME_EXTRA_VARS_MAP,
  type ThemeRow,
  type ThemeTokens,
  TOKEN_TO_CSS_MAP,
  updateBlurMutation,
  updateFollowDeviceThemeMutation,
  updateGlobalThemeMutation,
  VISITOR_ID,
} from "@cartero/shared";

type BackgroundStyle = "gradients" | "lines";

/**
 * Canonical color mode, available to EVERYONE (with and without login). It is ORTHOGONAL
 * to the active theme (default or custom): the mode only picks the light/dark VARIANT
 * VARIANT of the theme, without changing it.
 *  - "system": the variant follows the OS (prefers-color-scheme).
 *  - "light"/"dark": an EXPLICIT override that BEATS the OS.
 * It is persisted in localStorage (per device) and is also read by the
 * index.html pre-paint script to avoid a flash on reload.
 *
 * Re-exported from @cartero/shared, where the pure `resolveTheme` logic lives.
 */
export type { ColorMode };

/** localStorage key for the color mode (prefixed per app). */
export const COLOR_MODE_STORAGE_KEY = "color-mode";

interface ThemeContextValue {
  tokens: () => ThemeTokens | null;
  isDark: () => boolean;
  isBlur: () => boolean;
  followDevice: () => boolean;
  colorMode: () => ColorMode;
  /**
   * Sets the canonical mode (localStorage) WITHOUT touching the active theme. Mode and
   * theme are ORTHOGONAL axes: changing mode preserves the custom theme.
   */
  selectColorMode: (mode: ColorMode) => void;
  /** Sets only the mode (localStorage) without touching the custom theme. Alias of selectColorMode. */
  setColorMode: (mode: ColorMode) => void;
  /** Reverts to the DEFAULT THEME (clears the global custom theme) without touching the mode. */
  resetToDefaultTheme: () => void;
  toggleBlur: () => Promise<void>;
  toggleFollowDevice: () => Promise<void>;
  allThemes: () => ThemeRow[] | undefined;
  systemIsDark: () => boolean;
  isThemeLoading: () => boolean;
  backgroundStyle: () => BackgroundStyle;
  setBackgroundStyle: (style: BackgroundStyle) => void;
  toggleBackgroundStyle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>();

function serializeCSSValue(value: unknown): string {
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }

  // oxlint-disable-next-line typescript/no-base-to-string
  return String(value);
}

function applyCSSVars(
  root: HTMLElement,
  source: ThemeTokens | DesignTokens,
  isDark: boolean,
  isBlur: boolean,
) {
  for (const [tokenKey, cssVar] of Object.entries(TOKEN_TO_CSS_MAP)) {
    // Runtime property access — avoids index-signature casts.
    // getOwnPropertyDescriptor returns the raw value typed as unknown.
    const value: unknown = Object.getOwnPropertyDescriptor(source, tokenKey)?.value;
    if (value === undefined || value === null) {
      continue;
    }
    const serialized = serializeCSSValue(value);
    root.style.setProperty(cssVar, serialized);

    // Expand to multi-mapped variables
    const extraVars = (THEME_EXTRA_VARS_MAP as Record<string, string[] | undefined>)[tokenKey];
    if (extraVars) {
      for (const ev of extraVars) {
        root.style.setProperty(ev, serialized);
      }
    }
  }
  root.style.setProperty("--is-dark", isDark ? "1" : "0");
  root.style.setProperty("--is-blur", isBlur ? "1" : "0");
}

export const ThemeProvider: ParentComponent = (props) => {
  const { db: psDb } = usePowerSyncContext();
  // Use session.userId (reactive function) instead of session.data?.user.id.
  // session.data is evaluated once at call-time (not a reactive getter), so on
  // page refresh — when the session machine is still priming from localStorage —
  // session.data is undefined and userId() would forever return undefined,
  // keeping theme queries disabled. session.userId() reacts correctly to the
  // machine finishing its priming phase.
  const { userId } = useSession();
  const drizzle = useDrizzle();
  const { preferences, isLoading: isPreferencesLoading } = useUserPreferences();

  // Ensure the current user has a settings record in the user table.
  // In visitor/guest mode, there is no server synchronization, so no user rows exist.
  // We must seed/insert a default row for the current userId to enable theme queries and mutations.
  //
  // Guard: only VISITOR_ID reaches the seed below. Real accounts already get
  // their `user` row created server-side, unconditionally, on every signup/OAuth
  // path (packages/core-rust/src/db/user.rs:118,367 — `INSERT INTO "user"`).
  // Seeding locally for a real uid raced that server-created row during the
  // PowerSync checkpoint window: two inserts for the same id, one of them a
  // 3-column ghost — this was the root cause the §4.11 `patchRowVerified`
  // guard on `users/mutations.ts` has to defend against. Do NOT widen this
  // back to "any uid" — see
  // packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts,
  // which proves removing the seed for real accounts causes zero visual
  // regression (both branches collapse to the same `themeRow: null`) while it
  // closes the race. Same pattern as i18n-provider.tsx and
  // typography-provider.tsx (`userId !== VISITOR_ID` / `userId === VISITOR_ID`).
  createEffect(() => {
    const db = drizzle();
    const uid = userId();
    if (!db || uid !== VISITOR_ID) {
      return;
    }
    void (async () => {
      try {
        const existing = await db.select().from(user).where(eq(user.id, uid)).limit(1);
        if (existing.length === 0) {
          try {
            await db.insert(user).values({
              id: uid,
              is_blur: 1,
              follow_device_theme: 1,
            });
            console.info(`[ThemeProvider] Seeded default user settings row for ${uid}`);
          } catch (insertErr) {
            const errMsg = insertErr instanceof Error ? insertErr.message : String(insertErr);
            // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): error message check, not UI
            if (errMsg.includes("UNIQUE constraint failed")) {
              console.info(
                `[ThemeProvider] User settings row for ${uid} was created concurrently by sync`,
              );
              return;
            }
            throw insertErr;
          }
        }
      } catch (err) {
        console.error("[ThemeProvider] Failed to ensure user settings row:", err);
      }
    })();
  });

  // Detect system dark mode and react to OS changes
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): CSS media query, not UI
  const mq = globalThis.matchMedia("(prefers-color-scheme: dark)");
  const [systemIsDark, setSystemIsDark] = createSignal(mq.matches);
  const handleMqChange = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
  mq.addEventListener("change", handleMqChange);
  onCleanup(() => {
    mq.removeEventListener("change", handleMqChange);
  });

  // Query for the user's active theme (global + all list themes via UNION ALL).
  // We do NOT include currentGlobalThemeId() in the queryKey even though it is
  // available from userSettingsQuery. Adding it caused a double-invalidation race:
  //   1. onChangeWithCallback fires for the `user` table → invalidates current key
  //      → refetch → correct theme ✓
  //   2. userSettingsQuery refetches → currentGlobalThemeId() updates → queryKey
  //      changes → TanStack creates an empty new cache entry → data = undefined
  //      → tokens() = null → CSS vars freeze at the previous theme (one behind ✗)
  // getUnionThemeQuery already JOINs `user`, so resolveTables monitors it and
  // onChangeWithCallback fires reliably whenever global_theme_id changes.
  const themeQuery = createPowerSyncQuery<ThemeRow>({
    queryKey: () => queryKeys.themes.active(userId()),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      const uid = userId();
      if (!uid || !db) {
        return null;
      }
      return getUnionThemeQuery(db, uid);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    // Non-suspending — always-mounted provider. Suspending here would blank the
    // whole shell behind the root <Suspense> until the DB/theme query resolves.
    suspense: false,
  });

  const [optimisticBlur, setOptimisticBlur] = createSignal<boolean | null>(null);
  const [optimisticFollowDevice, setOptimisticFollowDevice] = createSignal<boolean | null>(null);
  const savedBgStyle = localStorage.getItem("bg-style");
  const [backgroundStyleState, setBackgroundStyleState] = createSignal<BackgroundStyle>(
    savedBgStyle === "gradients" || savedBgStyle === "lines" ? savedBgStyle : "gradients",
  );

  // Canonical color mode (system/light/dark), per device. Available without login.
  const savedColorMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  const [colorMode, setColorModeState] = createSignal<ColorMode>(
    savedColorMode === "light" || savedColorMode === "dark" ? savedColorMode : "system",
  );

  const isBlur = createMemo(() => optimisticBlur() ?? preferences()?.is_blur !== 0);

  const followDevice = createMemo(
    () => optimisticFollowDevice() ?? Boolean(preferences()?.follow_device_theme),
  );

  // Retain the last non-empty theme rows. A PowerSync invalidation burst (the
  // EXPLAIN/resolveTables waterfall seen when any table changes) can momentarily
  // leave `themeQuery.data` empty/undefined mid-refetch. Downstream consumers —
  // AppBackground's glow colors (`getGlowColors`) and `useAppThemeState` —
  // would then fall back to hardcoded per-route glows and
  // flash. Holding the previous rows keeps the resolved theme stable until real
  // data lands. A genuine theme change still carries rows, so it passes through.
  let lastThemes: ThemeRow[] | undefined = undefined;
  const allThemes = createMemo<ThemeRow[] | undefined>(() => {
    const { data } = themeQuery;
    if (data && data.length > 0) {
      lastThemes = data;
      return data;
    }
    return lastThemes ?? data;
  });

  // CANONICAL theme resolution (pure logic, unit-tested in @cartero/shared).
  // Two orthogonal axes: (a) WHICH theme (global custom or default) and (b) WHICH
  // mode (system/light/dark). The explicit mode BEATS the OS; the theme is
  // PRESERVED when the mode changes. `resolveTheme` picks the light/dark variant
  // of the theme (using its light/dark pair if one exists).
  const resolved = createMemo(() => {
    const globalRow = allThemes()?.find((r) => r.target_id === "global") ?? null;
    // A global row without id (leftJoin with no theme selected) = default theme.
    const themeRow: ThemeRow | null = globalRow?.id ? globalRow : null;
    return resolveTheme({
      themeRow,
      mode: colorMode(),
      systemPrefersDark: systemIsDark(),
      isBlurContext: isBlur(),
      parentTokens: null,
    });
  });

  const tokens = createMemo<ThemeTokens | null>(() => resolved().tokens);
  const isDark = createMemo(() => resolved().effectiveMode === "dark");

  // Apply CSS variables and dark/light class to <html>
  //
  // The effect also re-runs when an identical theme row re-emits (every route
  // change does it). A view transition hides the page from hit-testing for its
  // whole duration, so it only runs when what was applied actually changed.
  let appliedSignature: string | undefined = undefined;
  createEffect(() => {
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: applyTheme runs later inside document.startViewTransition and must apply the values of this effect run
    const r = resolved();
    const dark = r.effectiveMode === "dark";
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: applyTheme runs later inside document.startViewTransition and must apply the values of this effect run
    const blur = isBlur();
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: applyTheme runs later inside document.startViewTransition and must apply the values of this effect run
    const mode = colorMode();
    const root = document.documentElement;
    const signature = JSON.stringify([dark, blur, mode, r.tokens]);
    const changed = appliedSignature !== undefined && signature !== appliedSignature;
    appliedSignature = signature;

    const applyTheme = () => {
      // The mode is reflected as a class (.light/.dark) AND as a data-mode attribute on
      // <html>. The explicit class BEATS any @media (prefers-color-scheme)
      // — that is why the explicit mode supersedes the OS. `color-scheme` aligns the
      // native controls (scrollbars, inputs) with the effective variant.
      root.style.setProperty("color-scheme", dark ? "dark" : "light");
      root.classList.toggle("dark", dark);
      root.classList.toggle("light", !dark);
      root.dataset.mode = mode;
      // `resolveTheme` already delivers the correct tokens (base for the default
      // theme, or the light/dark variant of the active custom theme).
      applyCSSVars(root, r.tokens, dark, blur);
    };

    if (
      changed &&
      "startViewTransition" in document &&
      typeof document.startViewTransition === "function"
    ) {
      const transition = document.startViewTransition(applyTheme);
      // Catch skipped/aborted transitions to prevent uncaught promise rejection warnings
      void (async () => {
        try {
          await transition.ready;
        } catch {
          // ignore skipped transition error
        }
        try {
          await transition.finished;
        } catch {
          // ignore skipped transition error
        }
      })();
    } else {
      applyTheme();
    }
    // `allThemes` retains the last non-empty rows, so during a PowerSync refetch
    // `resolved()` keeps delivering the custom theme (it does not flash to the base).
  });

  const toggleBlur = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    const newValue = !isBlur();
    setOptimisticBlur(newValue);
    try {
      await updateBlurMutation(db, uid, newValue);
    } catch (err) {
      setOptimisticBlur(null);
      console.error("Error toggling blur:", err);
    }
  };

  const toggleFollowDevice = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    const newValue = !followDevice();
    setOptimisticFollowDevice(newValue);
    try {
      await updateFollowDeviceThemeMutation(db, uid, newValue);
    } catch (err) {
      setOptimisticFollowDevice(null);
      console.error("Error toggling follow device:", err);
    }
  };

  const setColorMode = (mode: ColorMode) => {
    setColorModeState(mode);
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  };

  // Mode and theme are ORTHOGONAL: choosing a canonical mode does NOT touch the active theme.
  // The custom theme is preserved; the mode only picks its light/dark variant.
  // (To go back to the default theme there is `resetToDefaultTheme`.)
  const selectColorMode = (mode: ColorMode) => {
    setColorMode(mode);
  };

  // Reverts to the DEFAULT THEME by clearing the global custom theme, without touching
  // the mode (the chosen light/dark axis is kept).
  const resetToDefaultTheme = () => {
    const db = drizzle();
    const uid = userId();
    if (db && uid) {
      void updateGlobalThemeMutation(db, uid, null).catch((err: unknown) => {
        console.error("Error clearing custom theme:", err);
      });
    }
  };

  const setBackgroundStyle = (style: BackgroundStyle) => {
    setBackgroundStyleState(style);
    localStorage.setItem("bg-style", style);
  };

  const toggleBackgroundStyle = () => {
    setBackgroundStyle(backgroundStyleState() === "gradients" ? "lines" : "gradients");
  };

  return (
    <ThemeContext.Provider
      value={{
        tokens,
        isDark,
        isBlur,
        followDevice,
        colorMode,
        selectColorMode,
        setColorMode,
        resetToDefaultTheme,
        toggleBlur,
        toggleFollowDevice,
        allThemes,
        systemIsDark,
        isThemeLoading: () => isPreferencesLoading() || themeQuery.isLoading,
        backgroundStyle: backgroundStyleState,
        setBackgroundStyle,
        toggleBackgroundStyle,
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
};
